/**
 * Help Center content (approved mockup outputs/help-center-mockup-20261029.html, Bill 2026-10-29/30).
 * Written by us and shipped with each release, the same for every company: not Vault documents,
 * so it works for SaaS customers too. Every step is checked against the app before it ships.
 * Text uses **bold** for the names of buttons, fields and pages; nothing else is parsed.
 * Videos live in frontend/public/help/videos/ (recorded from the app with a test company and
 * test people, never real client data; scripts in assets/video-pilot/ on Bill's folder).
 */

export type HelpPart = "Vault" | "Workflows" | "TechHub" | "ReviewHub" | "VendorHub" | "Admins" | "Everyone";

export interface HelpVideo {
  id: string;
  title: string;
  part: HelpPart;
  /** Length in seconds. */
  secs: number;
  /** The article it belongs to. */
  article: string;
  /** Chapters: start in seconds, and a name. */
  chapters?: [number, string][];
}

export type Block =
  | { h: string }
  | { p: string }
  | { ol: string[] }
  | { ul: string[] }
  | { shot: string; cap: string };

export interface HelpArticle {
  id: string;
  topic: string;
  title: string;
  applies: string;
  updated: string;
  summary: string;
  video?: string;
  /** A walkthrough in parts: one player each, with chapters. */
  videos?: string[];
  /** Only company admins see it in lists and search, and can open it. */
  admin?: boolean;
  body: Block[];
  good?: string[];
  related?: string[];
  /** Extra words people might search for. */
  keys?: string;
}

export interface HelpTopic {
  id: string;
  title: string;
  desc: string;
}

const V = "/help/videos/";
export const videoSrc = (id: string) => `${V}${id}.mp4`;
export const posterSrc = (id: string) => `${V}${id}.jpg`;
export const thumbSrc = (id: string) => `${V}${id}-thumb.jpg`;
export const shotSrc = (id: string) => `${V}${id}-shot.jpg`;
export const dur = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

export const VIDEOS: HelpVideo[] = [
  { id: "acknowledge", title: "Read and acknowledge a policy", part: "Vault", secs: 30, article: "acknowledge" },
  { id: "find", title: "Find a document fast", part: "Vault", secs: 34, article: "find" },
  { id: "revision", title: "Write a revision and send it for approval", part: "Vault", secs: 35, article: "revision" },
  { id: "approve", title: "Review and approve a document", part: "Vault", secs: 32, article: "approve" },
  { id: "onboarding", title: "Start employee onboarding", part: "Workflows", secs: 35, article: "onboarding" },
  { id: "add-vendor", title: "Add a vendor and its SOC 2 report", part: "VendorHub", secs: 53, article: "add-vendor" },
  { id: "new-document", title: "Write a new document", part: "Vault", secs: 35, article: "new-document" },
  { id: "suggest-change", title: "Suggest a change to a document", part: "Vault", secs: 30, article: "suggest-change" },
  { id: "policy-review", title: "The annual policy review", part: "Workflows", secs: 33, article: "policy-review" },
  { id: "add-client", title: "Add a client and its people", part: "TechHub", secs: 42, article: "add-client" },
  { id: "quarterly-review", title: "Run a quarterly review", part: "ReviewHub", secs: 39, article: "quarterly-review" },
  { id: "qr-walkthrough-1", title: "Part 1 of 3: Set up and start", part: "ReviewHub", secs: 240, article: "quarterly-review-walkthrough",
    chapters: [[0, "Welcome and the five stages"], [28, "ReviewHub settings"], [101, "Before you start a review"], [126, "The Reviews list"], [165, "Start the review"], [193, "Intake: book the meeting"]] },
  { id: "qr-walkthrough-2", title: "Part 2 of 3: Prepare the review", part: "ReviewHub", secs: 286, article: "quarterly-review-walkthrough",
    chapters: [[0, "Welcome"], [13, "Evidence"], [78, "Tech Prep"], [103, "Scorecard"], [159, "Risks"], [190, "Roadmap"], [230, "The gates and the Pitch check"]] },
  { id: "qr-walkthrough-3", title: "Part 3 of 3: The meeting and after", part: "ReviewHub", secs: 215, article: "quarterly-review-walkthrough",
    chapters: [[0, "Welcome"], [7, "The client page"], [60, "Move to Review and present"], [77, "Pulse notes"], [94, "Commitments"], [130, "Give the client their page"], [151, "Close out and hold"], [179, "What lands in My tasks"]] },
];

export const TOPICS: HelpTopic[] = [
  { id: "start", title: "Getting started", desc: "The basics, for your first week." },
  { id: "docs", title: "Documents and approvals", desc: "Finding, reading, writing and approving controlled documents." },
  { id: "workflows", title: "Workflows", desc: "Checklists that run themselves, like onboarding." },
  { id: "techhub", title: "TechHub", desc: "Your clients, their people, Microsoft 365 and agreements. An add-on; ask your admin if you don't see it." },
  { id: "reviewhub", title: "ReviewHub", desc: "Quarterly business reviews built from what DocVault already knows. An add-on; needs TechHub too." },
  { id: "vendorhub", title: "VendorHub", desc: "Vendors, the data they touch and their evidence. An add-on; ask your admin if you don't see it." },
  { id: "integrations", title: "Integrations", desc: "Connecting the tools you already use." },
  { id: "account", title: "Account and security", desc: "Signing in, roles and people." },
];

/** "Start here" on the Help Center home (Bill, 2026-10-30), with one line on why each matters. */
export const FEATURED: { video: string; why: string }[] = [
  { video: "acknowledge", why: "The one thing every person in DocVault does." },
  { video: "find", why: "Acronyms, initials and document numbers all work." },
  { video: "approve", why: "Read what readers will see, then sign it off." },
];

/** Common searches shown under the search box. */
export const POPULAR = ["acknowledge a policy", "approve", "start date", "lost my phone", "SOC 2"];

const UPD = "Oct 30, 2026";

export const ARTICLES: HelpArticle[] = [
  // ------------------------------------------------------------------ Getting started
  {
    id: "what-docvault-is-for", topic: "start", title: "What DocVault is for", applies: "Everyone", updated: UPD,
    summary: "DocVault keeps your company's policies, procedures and forms in one place, with one approved version of each. Everyone reads the same thing, every approval and acknowledgment is recorded, and add-ons hold the rest of what an IT provider has to keep track of.",
    body: [
      { h: "The Vault" },
      { p: "The **Library** holds your controlled documents. Authors write them, an approver signs them off, and only then do readers see them. Each one has a document ID that never changes and a version that goes up with every approved change." },
      { ul: [
        "Readers always see the current approved version, as a PDF copy stamped with their name.",
        "Policies can ask people to read and acknowledge them. DocVault records who acknowledged which version, and when.",
        "Every view, download and decision goes in the audit log.",
        "Documents come up for review on a schedule, so nothing quietly goes stale.",
      ] },
      { h: "Workflows" },
      { p: "Checklists that run themselves, like employee onboarding and offboarding and the annual policy review. Each step lands in the right person's **My tasks**, and some steps tick themselves when DocVault can see the work is done." },
      { h: "Add-ons" },
      { ul: [
        "**TechHub**: your clients, their people, devices, networks, agreements and Microsoft 365 tenants.",
        "**ReviewHub**: quarterly business reviews built from what DocVault already knows.",
        "**VendorHub**: your vendors, the data they touch, their SOC 2 reports and other evidence, and their risk.",
      ] },
      { p: "Your company admin decides which add-ons you see and what you can do in each. See **Roles: who can do what**." },
    ],
    related: ["my-tasks", "find", "roles"],
    keys: "overview introduction about vault library add-ons techhub reviewhub vendorhub",
  },
  {
    id: "my-tasks", topic: "start", title: "My tasks: what's waiting on you", applies: "Everyone", updated: UPD,
    summary: "My tasks is one list of everything that needs you: documents to read and acknowledge, approvals, review dates, and workflow steps. Start your day there.",
    body: [
      { h: "What shows up" },
      { ul: [
        "**Read and acknowledge**: a policy your company asks people in your role to confirm they've read.",
        "**Approvals**: a new document or revision waiting on you as a reviewer or approver.",
        "**Reviews due**: a document you own that's coming up for its yearly review.",
        "**Workflow steps**: a step in onboarding, offboarding or another workflow that's assigned to you.",
      ] },
      { h: "Work through it" },
      { ol: [
        "Open **My tasks** from the menu on the left. The number next to it is how many things are waiting.",
        "Use the buttons at the top to show everything or just one part, such as Vault or VendorHub.",
        "Click the button on a row to open it. Most tasks tick themselves when you finish, for example when you acknowledge the policy or approve the revision.",
        "Finished tasks move to **Done this week** at the bottom.",
      ] },
      { h: "How you hear about tasks" },
      { p: "DocVault emails you when a task shows up. If it waits, you get a reminder 3 days later, then once a week until it's done. Turn on **Morning summary** to get one email about 8 AM Eastern listing everything instead. Click **Notifications** at the top of My tasks to change these." },
    ],
    good: [
      "If you work for more than one company, the company picker above the menu changes which company's tasks you see. **All my companies** shows them all.",
      "Overdue tasks are counted at the top of the page.",
    ],
    related: ["acknowledge", "approve", "onboarding"],
    keys: "to do inbox reminders notifications email digest overdue",
  },
  // ------------------------------------------------------------------ Documents and approvals
  {
    id: "find", topic: "docs", title: "Find a document fast", applies: "Everyone", updated: UPD, video: "find",
    summary: "The search box at the top of every page understands acronyms, initials and document numbers, so IR finds the Incident Response Policy and SEC-002 goes straight to that document. The Library narrows the list by area, type and classification.",
    body: [
      { h: "Search from anywhere" },
      { ol: [
        "Click the search box at the top, or press **Ctrl K** (or **/**) on any page.",
        "Type a few letters: part of a title, words from the document, an acronym like **IR** or **BCP**, or part of a document number like **SEC-002**.",
        "Use the arrow keys to pick a result and press **Enter** to open it.",
      ] },
      { shot: "find", cap: "IR finds the Incident Response Policy. The result shows why it matched." },
      { h: "Browse the Library" },
      { ol: [
        "Open **Library** from the menu on the left.",
        "Use **Area**, **Type**, **Classification** and **Framework** to narrow the list, or type in the box next to them.",
        "**Review due** shows documents coming up for their yearly review.",
        "Click a document to open it.",
      ] },
    ],
    good: [
      "Readers only ever see current approved versions. Authors and approvers also see drafts and revisions in progress.",
      "Company admins add your own acronyms in **Settings**, under **Search acronyms**, so search knows what yours mean.",
    ],
    related: ["acknowledge", "what-docvault-is-for"],
    keys: "search palette ctrl k acronym number library filter area type classification",
  },
  {
    id: "acknowledge", topic: "docs", title: "Read and acknowledge a policy", applies: "Everyone", updated: UPD, video: "acknowledge",
    summary: "Some policies ask you to confirm you've read them. They show up in My tasks. Open the policy, read the current version, tick the box and click Acknowledge. DocVault records who acknowledged which version, and when.",
    body: [
      { ol: [
        "Open **My tasks**. Policies to read show as **Read and acknowledge**, with the document number and title.",
        "Click **Read**. The policy opens at its current approved version.",
        "Read it. A green bar at the top asks you to acknowledge it.",
        "Tick **I have read and understood this document.** and click **Acknowledge**.",
      ] },
      { shot: "acknowledge", cap: "Once you've acknowledged it, the policy shows Acknowledged next to its version." },
      { p: "The task leaves your list and the policy shows **Acknowledged** at the top whenever you open it." },
    ],
    good: [
      "When a policy changes in a major or minor way, you're asked to acknowledge the new version. A small fix, such as a typo, doesn't ask again.",
      "Your company decides which roles are asked. You'll see who's asked under **Acknowledgment** in the document's details.",
      "Think something in the policy should change? Use **Suggest a change** at the top of the document. The owner or an approver decides, and you hear back either way.",
    ],
    related: ["my-tasks", "find"],
    keys: "acknowledgement acknowledgment sign read confirm policy attest",
  },
  {
    id: "revision", topic: "docs", title: "Write a revision and send it for approval", applies: "Authors", updated: UPD, video: "revision",
    summary: "To change a published document, check it out, upload the new file, say what changed and submit it. The current version stays live for readers until an approver signs off on the new one.",
    body: [
      { h: "Check it out" },
      { ol: [
        "Open the document and click **Check out**. Nobody else can start a revision while you have it.",
        "A blue bar shows it's checked out to you. Click **Upload new revision** when your new file is ready, or **Cancel check-out** to let it go.",
      ] },
      { h: "Upload and submit" },
      { ol: [
        "Drop the new file on **Upload the new file**, or click it to choose one. Word, Excel, PowerPoint, Visio and PDF all work.",
        "Under **Size of the change**, pick **Major**, **Minor** or **Revision** (a typo or formatting fix). This sets the next version number.",
        "In **What changed**, say what's different. It goes in the revision history.",
        "Click **Submit for approval**, or **Save draft** to finish later.",
      ] },
      { shot: "revision", cap: "The new version page: the file, who signs it off, the size of the change and what changed." },
      { p: "The document shows the new version as **In review**, and the approvers get it in their tasks. You'll hear when it's approved or if changes are requested." },
    ],
    good: [
      "Readers keep seeing the current version until the new one is approved, so there's never a gap.",
      "Most companies don't let authors approve their own work. If you're your company's only approver, the record says Self-approved.",
      "Your drafts are under **My drafts** in the menu.",
    ],
    related: ["approve", "find"],
    keys: "edit update change new version check out checkout upload submit draft",
  },
  {
    id: "approve", topic: "docs", title: "Review or approve a document", applies: "Approvers and reviewers", updated: UPD, video: "approve",
    summary: "Approvals lists everything waiting on you. Open one, read the new version exactly as readers will see it, then approve it or request changes. Your approval is recorded against that exact file.",
    body: [
      { ol: [
        "Open **Approvals** from the menu on the left, or the approval in **My tasks**.",
        "Click **Review & approve**. The document opens on the version in review. Switch between the current version and the one in review with the buttons above the document.",
        "Read it. **Size of the change** shows what the author proposed; change it if you disagree.",
        "Add a comment if you like, then click **Approve & publish**. To send it back, write what needs to change and click **Request changes**.",
      ] },
      { shot: "approve", cap: "The approval panel: the activity so far, the file and its fingerprint, and the size of the change." },
      { p: "Once approved, the new version is published at once and readers see it. To publish on a later date, set an **Effective date** before you approve." },
    ],
    good: [
      "Some documents need a review before approval, or more than one approver. Your company admin sets this in **Settings**, under **Reviews & approvals**.",
      "You can't approve your own work unless your company allows it.",
      "Every decision, with its comment, is in the document's **Activity** and the audit log.",
    ],
    related: ["revision", "roles"],
    keys: "approval approver reviewer sign off publish request changes reject",
  },
  // ------------------------------------------------------------------ Workflows
  {
    id: "onboarding", topic: "workflows", title: "Employee onboarding and offboarding", applies: "Company admins and managers", updated: UPD, video: "onboarding",
    summary: "Add a new person on the Team page and Employee onboarding starts by itself. Set their manager and start date, and each step lands in the right person's My tasks with a due date. Setting a last day starts offboarding the same way.",
    body: [
      { h: "Start onboarding" },
      { ol: [
        "Go to **Settings**, then **Team**.",
        "Under **Add or invite someone**, enter their work email and name, pick any extra roles under **Also make them**, and click **Add / invite**.",
        "Find them in the list and click **Details**. Set their **Manager** and **Start date**, then **Save**.",
      ] },
      { p: "Employee onboarding starts as soon as they're added. The due dates follow the start date, so if it changes later, the open steps move with it." },
      { h: "Follow it" },
      { ol: [
        "Open **Workflows** and click the person's onboarding.",
        "Steps are grouped by when they're due, with who does each one. **Who has what** shows how many are open for each person.",
        "Tick a step when it's done. Some tick themselves: reading a policy ticks when they acknowledge it, and signing in ticks when they sign in and set up two-step sign-in.",
      ] },
      { shot: "onboarding", cap: "An onboarding in progress: each step, who it's for and when it's due." },
      { h: "Offboarding" },
      { p: "In the person's **Details** on the Team page, set a **Last day** and save. Employee offboarding starts by itself. Removing them from the company ticks its own step." },
    ],
    good: [
      "Manager steps go to the manager you set. Without one, they go to the company admins.",
      "Company admins change the steps in **Settings**, under **Workflows**.",
      "The person being offboarded doesn't see their own offboarding.",
    ],
    related: ["add-people", "my-tasks", "roles"],
    keys: "new hire starter leaver offboarding checklist manager start date last day",
  },
  // ------------------------------------------------------------------ VendorHub
  {
    id: "add-vendor", topic: "vendorhub", title: "Add a vendor and its SOC 2 report", applies: "VendorHub editors and company admins", updated: UPD, video: "add-vendor",
    summary: "Each vendor is one record that every client who uses it shares. You say what the vendor does and the data it touches; DocVault works out what evidence that calls for. Upload a SOC 2 report once and it counts everywhere.",
    body: [
      { h: "Add the vendor" },
      { ol: [
        "In VendorHub, open **Vendors** and click **+ Add a vendor**.",
        "Enter the name, a category and what they do for you or your clients.",
        "Tick the data they touch: **PHI**, **PII**, **Payment** or **CUI**. Leave all unticked if they touch no sensitive data.",
        "Pick the criticality and how MFA works for their users, then click **Add**.",
      ] },
      { p: "The vendor's page opens. Under **Evidence**, a row for each kind of data shows what's needed and whether it's in place." },
      { h: "Upload the SOC 2 report" },
      { ol: [
        "On the vendor's page, click **+ Add evidence**.",
        "Pick **SOC 2 Type II report** and enter the date its period ends.",
        "Choose the file and click **Add**.",
      ] },
      { shot: "add-vendor", cap: "Once the report is in, each row reads Current and the risk drops to Low." },
    ],
    good: [
      "A report counts for as many months after its period end as your rules say. You'll get a task before it runs out.",
      "Only people with VendorHub can open the file, and every opening is in the audit log.",
      "No file yet? Add the evidence anyway and say in **Notes** where it's kept, for example the vendor's trust portal.",
    ],
    related: ["roles", "what-docvault-is-for"],
    keys: "vendor supplier soc soc2 soc 2 evidence risk third party baa",
  },
  // ------------------------------------------------------------------ Integrations
  {
    id: "connect-m365", topic: "integrations", title: "Connect Microsoft 365", applies: "Company admins with TechHub", updated: UPD,
    summary: "One app registration in your own Microsoft Entra ID lets DocVault read licenses, users, Secure Score and MFA settings from each client's Microsoft 365 tenant. It's read only. You register it once, then each client's admin approves it for their tenant.",
    body: [
      { h: "Register the app (once)" },
      { ol: [
        "In DocVault, go to **Settings**, then **Integrations**, then **Microsoft 365**, and click **Set up**. It shows the exact values to copy.",
        "In the Microsoft Entra admin center, open **Entra ID**, then **App registrations**, then **New registration**. A Global or Application Administrator does this; it takes about 10 minutes.",
        "Use your company's name for the app, since your clients' admins see it when they approve. Under supported account types, choose **Multiple Entra ID tenants**, then **Allow all tenants**.",
        "Add the Microsoft Graph **Application** permissions DocVault lists.",
        "Under **Certificates & secrets**, create a new client secret for 24 months. Copy its **Value** right away; Microsoft shows it only once.",
      ] },
      { h: "Connect it" },
      { ol: [
        "Back in DocVault, paste the **Application (client) ID**, the **Directory (tenant) ID** and the secret value.",
        "Click **Test and save**.",
        "Connect each client from their page in TechHub: **Technology**, then **Microsoft 365**, then **Connect a tenant**. The client's admin approves the app for their tenant.",
      ] },
    ],
    good: [
      "Every permission is read only: licenses, users, Secure Score, MFA methods and sign-in policies. Nothing sends mail, changes anything or reads mailboxes or files.",
      "The secret is stored encrypted. Nobody can read it back, not even admins; you can only replace it. DocVault reminds admins before it expires.",
      "When you renew the secret, the old one keeps working until the new one is saved, so there's no gap.",
      "If your company keeps its own setup guide in the Vault, an admin can link it with **Link setup guide** on the Microsoft 365 tab.",
    ],
    related: ["roles", "two-step"],
    keys: "microsoft 365 m365 office entra azure graph tenant licenses secure score integration",
  },
  // ------------------------------------------------------------------ Account and security
  {
    id: "two-step", topic: "account", title: "Two-step sign-in", applies: "Everyone", updated: UPD,
    summary: "DocVault always asks for a second step when you sign in. If you sign in with Microsoft, your company's Microsoft sign-in does it. If you sign in with a password, DocVault asks for a 6-digit code from an authenticator app on your phone.",
    body: [
      { h: "Set it up" },
      { p: "The first time you sign in with a password, DocVault walks you through it:" },
      { ol: [
        "Get an authenticator app: Microsoft Authenticator, Google Authenticator, 1Password or any app that shows 6-digit codes.",
        "Scan the code on the screen with the app. Can't scan? Choose \"Enter a setup key\" in the app and type the key shown.",
        "Enter the 6-digit code the app shows and click **Turn on and continue**.",
      ] },
      { h: "New phone" },
      { ol: [
        "Open the menu under your name and choose **Sign-in & security**.",
        "Click **Move to a new phone** and follow the same three steps with the new phone. The old phone stops working.",
      ] },
      { h: "Lost your phone?" },
      { p: "Ask your company admin to click **Reset two-step** next to your name on the Team page. You'll get an email and set it up again at your next sign-in." },
    ],
    good: [
      "Lost a laptop or used a shared computer? In **Sign-in & security**, sign out everywhere ends your DocVault sessions on every browser and device.",
      "On the Team page, admins can see who hasn't set up two-step sign-in yet.",
    ],
    related: ["roles", "add-people"],
    keys: "mfa 2fa multi-factor authenticator code otp totp phone sign in login password",
  },
  {
    id: "roles", topic: "account", title: "Roles: who can do what", applies: "Everyone", updated: UPD,
    summary: "Everyone in a company can read its approved documents. Roles add what else you can do, grouped by part: the Vault, then each add-on. You can see your own roles in My profile, under Companies & roles.",
    body: [
      { h: "Vault" },
      { ul: [
        "**Reader**: every member. Reads approved documents and acknowledges policies.",
        "**Author**: writes new documents and revisions and sends them for approval.",
        "**Reviewer**: reviews documents before approval, when your company asks for a review step.",
        "**Approver**: approves documents, which publishes them. Every company needs at least one.",
        "**Admin**: manages the company's people, roles, settings and integrations. Admin doesn't include writing or approving; tick Author or Approver too if they do those.",
      ] },
      { h: "Add-ons" },
      { ul: [
        "**TechHub**: viewers see clients and add notes; editors also change client records and open signed agreements; **Accounting** sees and changes agreement dollar values and opens signed agreements, and nothing else.",
        "**ReviewHub**: viewers see the quarterly reviews; editors build them.",
        "**VendorHub**: viewers see vendors and open their evidence; editors add and change vendors, evidence and findings.",
        "Company admins are also ReviewHub and VendorHub admins.",
      ] },
    ],
    good: [
      "Roles are per company. If you work for more than one, you can have different roles in each.",
      "Only company admins change roles, and every change is in the audit log.",
    ],
    related: ["add-people", "two-step"],
    keys: "permissions access reader author reviewer approver admin viewer editor accounting",
  },
  {
    id: "add-people", topic: "account", title: "Add people and give them roles", applies: "Company admins", updated: UPD,
    summary: "The Team page is one list of your company's people, with their roles grouped by part. Add someone, tick their roles and save. New people get an email with a temporary password.",
    body: [
      { h: "Add someone" },
      { ol: [
        "Go to **Settings**, then **Team**.",
        "Under **Add or invite someone**, enter their work email and, if they're new to DocVault, their name.",
        "Under **Also make them**, pick any of **Author**, **Reviewer**, **Approver** or **Admin**.",
        "Click **Add / invite**.",
      ] },
      { p: "Everyone added can read the company's documents. People who already use DocVault just get access. Employee onboarding starts for them by itself." },
      { h: "Change roles" },
      { ol: [
        "Click a role's box in someone's row to turn it on or off. To see an add-on's roles, pick it at the top of the list.",
        "Click **Save changes**. Nothing applies until you save.",
      ] },
      { h: "More for each person" },
      { p: "**Details** on a person's row sets their manager, start date and last day, signs them out on every device, or removes them from the company. **Reset two-step** helps someone who lost their phone." },
    ],
    good: [
      "Every company needs at least one approver, and you can't remove your own admin role.",
      "Setting a last day starts Employee offboarding.",
      "Every change is in the audit log.",
    ],
    related: ["roles", "onboarding", "two-step"],
    keys: "invite user member team add person remove manager start date",
  },
  // ------------------------------------------------------------------ second batch (2026-10-30)

  // ------------------------------------------------------------------ Getting started
  {
    id: "switch-companies", topic: "start", title: "Switch between companies and apps", applies: "Everyone", updated: UPD,
    summary: "If you work for more than one company, the company picker at the top of the menu on the left decides whose documents and tasks you see. If you have add-ons, the app picker above it moves you between the Vault and each add-on.",
    body: [
      { h: "Pick a company" },
      { ol: [
        "Click the company name or logo near the top of the menu on the left.",
        "Pick a company. Each one lists your roles there.",
        "Pick **All my companies** to see everything at once. It only shows if you're in more than one company.",
      ] },
      { p: "The **Library**, search and **My tasks** now show that company. Company-wide documents always show, whichever company you pick. If you had a document open, you go back to the Library." },
      { h: "Choose where you land" },
      { ol: [
        "Open the menu under your name and choose **My profile**.",
        "Under **Default workspace**, pick a company or **All my companies**.",
        "Click **Save changes**. You land there each time you sign in.",
      ] },
      { h: "Switch apps" },
      { p: "If you have TechHub, ReviewHub or VendorHub, the app picker above the company picker shows the app you're in. Click it and pick one under **Switch to**. The menu on the left changes to match, and **My tasks** stays at the top in every app." },
    ],
    good: [
      "Roles are per company, so you might be an author in one and a reader in another.",
      "Company admins also see add-ons that aren't on yet, under **More add-ons**, marked **Not on**. Clicking one opens its page in **Settings**.",
      "Don't see the app picker? You only have the Vault, so there's nothing to switch to.",
    ],
    related: ["roles", "my-tasks", "find"],
    keys: "company picker workspace switch tenant all my companies app picker add-on techhub reviewhub vendorhub default",
  },
  {
    id: "notifications", topic: "start", title: "Email notifications and how to change them", applies: "Everyone", updated: UPD,
    summary: "DocVault emails you when something needs you or something you care about changes. You choose which emails you get in My profile, under Notifications. Every email says why you got it.",
    body: [
      { h: "Change what you get" },
      { ol: [
        "Open the menu under your name and choose **Notifications**.",
        "Turn each switch on or off. It saves as soon as you click it.",
        "For **Review reminders**, use **Remind me** to pick how far ahead you hear about a review: 14, 30, 60 or 90 days.",
      ] },
      { h: "What you can turn on or off" },
      { ul: [
        "**Approval requests**, **Changes requested** and **My document was approved**, if you're an approver or author.",
        "**Review reminders**: for documents you own. You hear at the time you picked, 7 days before, on the day, then weekly while it's overdue.",
        "**Documents to acknowledge**: when a policy you must read is published or changes.",
        "**Suggested changes**: when someone suggests a change to a document you own, and when your own suggestion is answered.",
        "**Weekly digest**: a Monday email with new and updated documents, reviews coming due and documents to acknowledge.",
        "**New documents published**: every new document in your companies. This one starts off.",
      ] },
      { h: "Reminders and the daily summary" },
      { p: "Under **My tasks**, **Reminder if it's still waiting** sends a reminder 3 days after something lands in My tasks, then once a week until it's done. **Daily summary** sends one email each morning listing everything instead of separate reminders, and is skipped on days with nothing new. **Done notices** tell you when something you were waiting on is finished." },
    ],
    good: [
      "You can also click **Notifications** at the top of **My tasks**. There they're called **Reminder if it waits** and **Morning summary**; they're the same switches.",
      "The daily summary only replaces the reminders. You still get the email for each approval, acknowledgment or review date.",
      "You only see the switches that apply to your roles. TechHub users also see agreement deadlines and SSL and domain expiry.",
    ],
    related: ["my-tasks", "acknowledge", "reviewed-no-changes"],
    keys: "email notification notify reminder digest weekly daily summary morning turn off unsubscribe alerts",
  },
  // ------------------------------------------------------------------ Documents and approvals
  {
    id: "new-document", topic: "docs", title: "Write a new document", applies: "Authors", updated: UPD, video: "new-document",
    summary: "Start with the file. DocVault reads the title from it and suggests a type, you fill in the rest and submit it. Nothing is published until an approver signs off, and you become the document's owner.",
    body: [
      { h: "Start with the file" },
      { ol: [
        "Click **New document** at the top right of the Dashboard, the **Library** or **My drafts**.",
        "Drop the file on **Start with the file**, or click it to choose one. Word, Excel, PowerPoint, Visio and PDF all work, up to 100 MB. How-Tos and SOPs can also be a video, up to 1 GB.",
        "DocVault fills in the title and suggests a type. Change either if it isn't right.",
      ] },
      { h: "Fill in the details" },
      { ol: [
        "Under **What kind of document?**, pick a type such as **Policy** or **SOP**, then the **Company** and **Area**. The type and area make up the document number.",
        "Under **Describe it**, check the **Title** and add a one-line **Description**. **Frameworks**, **Classification**, **Visibility** and **Retention** are optional.",
        "Under **Review and approval**, check **Next review due** and pick who it goes to in **Send to**, or leave it with any approver.",
        "Add **Notes for the approver** if there's something to look at closely.",
        "Click **Submit for approval**, or **Save draft** to finish later.",
      ] },
      { p: "The document page opens. Approvers get it in their tasks, and the first approval publishes it as v1.0.0." },
    ],
    good: [
      "The document ID, such as ITA-POL-SEC-001, is set when you save and never changes.",
      "**Next review due** starts at your company's review cycle, 12 months unless your admin changed it.",
      "A **Restricted** document can only be read by authors, reviewers, approvers and admins until you name other readers on its page.",
      "Saved drafts are under **My drafts** in the menu. Readers never see them.",
    ],
    related: ["revision", "approve", "request-changes"],
    keys: "create new document upload policy procedure sop how-to form draft submit number type area",
  },
  {
    id: "suggest-change", topic: "docs", title: "Suggest a change to a document", applies: "Everyone", updated: UPD, video: "suggest-change",
    summary: "Anyone who can read a published document can suggest a change in plain words. It goes to the document's owner. The owner, an approver or a company admin accepts or declines it, and you get an email either way.",
    body: [
      { h: "Send a suggestion" },
      { ol: [
        "Open the document and click **Suggest a change** at the top.",
        "In **Where**, say which part, for example a step number. This is optional.",
        "In **What should change**, say what's wrong or missing.",
        "Click **Send to the owner**.",
      ] },
      { p: "Your suggestion shows under **Suggestions** on the document, with its answer once it has one." },
      { h: "Answer a suggestion" },
      { p: "If you own the document, are an approver or are a company admin, suggestions show in **My tasks** as **Suggested change**. Click **Answer** to open the **Suggestions** tab." },
      { ul: [
        "**Accept and start a revision**: DocVault checks the document out to you and opens the new version page with the suggestion already in **What changed**. Upload the revised file and submit it.",
        "**Decline…**: write why, then click **Decline**. The person who suggested it sees your reason.",
      ] },
    ],
    good: [
      "You can suggest changes to published documents only, not drafts.",
      "You can have up to 5 open suggestions on one document.",
      "Suggestions are kept with the document. Accepting one doesn't change anything until a new revision is approved.",
    ],
    related: ["revision", "acknowledge", "notifications"],
    keys: "suggest suggestion feedback idea change request comment owner accept decline fix typo wrong",
  },
  {
    id: "request-changes", topic: "docs", title: "Request changes and resubmit", applies: "Authors, approvers and reviewers", updated: UPD,
    summary: "If something needs fixing, the approver or reviewer sends the document back with a comment. The author fixes it and submits it again. The current version stays live for readers the whole time.",
    body: [
      { h: "Send it back" },
      { ol: [
        "Open the document from **Approvals** or **My tasks**. The **Approval** tab opens.",
        "In **Comment**, say what needs to change. A comment is required.",
        "Click **Request changes**.",
      ] },
      { p: "The author gets an email and a **Changes requested** task in **My tasks**. A reviewer can send it back too, while it's waiting for a review." },
      { h: "Fix it and resubmit" },
      { ol: [
        "Click **Fix it** in **My tasks**, or **Fix and resubmit** in **My drafts**. A bar at the top of the document shows the comment.",
        "Click **Upload fixed version** and drop in the new file.",
        "Check the details under the file, such as **Send to**, then click **Submit for approval**.",
      ] },
      { p: "If the file doesn't need to change, open the **Approval** tab instead and click **Submit for approval** there. **Replace file** uploads a new one." },
    ],
    good: [
      "Only the person who uploaded the revision can fix and resubmit it.",
      "The comment and every resubmission stay in the **Approval** tab's history, so the approver can see what was asked.",
      "Changes requested always come first in **My drafts**.",
    ],
    related: ["approve", "revision", "new-document"],
    keys: "request changes send back reject rejected fix resubmit comment approver reviewer returned",
  },
  {
    id: "reviewed-no-changes", topic: "docs", title: "The yearly review: Reviewed, no changes", applies: "Document owners and company admins", updated: UPD,
    summary: "Every published document has a next review date. When it comes up, check the document is still right. If it is, mark it Reviewed, no changes, and the next review moves a full cycle on. If it isn't, write a revision.",
    body: [
      { h: "When a review comes up" },
      { p: "As the owner, you get an email before the review date. From 30 days before, it also shows in **My tasks** as **Review due**, or **Review overdue** once the date has passed." },
      { h: "Mark it reviewed" },
      { ol: [
        "Click **Review** in **My tasks**, or open the document.",
        "Read it and check it's still accurate.",
        "In the **Details** tab, find **Next review due** and click **Reviewed, no changes** next to it.",
        "Confirm. DocVault shows the new review date.",
      ] },
      { p: "The version stays the same, and the review task leaves your list. The confirmation is recorded in the audit log." },
      { h: "If it needs changes" },
      { p: "Check it out and write a revision instead. When the revision is approved, the next review is set a full cycle after it takes effect." },
    ],
    good: [
      "A full cycle is your company's review cycle, 12 months unless your admin changed it in **Settings**, under **Reviews & approvals**.",
      "Only the document's owner or a company admin sees **Reviewed, no changes**. The owner is shown in **Details**.",
      "Change how far ahead you're reminded in **Notifications**, under **Remind me**.",
    ],
    related: ["policy-review", "revision", "notifications"],
    keys: "periodic review annual yearly review date due overdue no changes still accurate owner cycle reviewed",
  },
  // ------------------------------------------------------------------ Workflows
  {
    id: "policy-review", topic: "workflows", title: "The annual policy review", applies: "Company admins and document owners", updated: UPD, video: "policy-review",
    summary: "The Annual policy review asks the owner of every published document to confirm it's still right, or revise it. Turn it on once and it starts every year on the date you set. Each document ticks itself off when its owner is done.",
    body: [
      { h: "Turn it on" },
      { ol: [
        "Go to **Settings**, then **Workflows**.",
        "Find **Annual policy review** and click **Edit**.",
        "Tick **Starts every year on the date below** and set **Yearly start (month-day)**, for example 10-15.",
        "Check how many days the owners have under **Due (days)**, then click **Save**.",
      ] },
      { p: "It starts the morning of that date each year. If the date has already passed this year, it starts the next morning. To start it yourself, see **Start a workflow by hand**." },
      { h: "What owners do" },
      { ol: [
        "The review shows in **My tasks** with how many of your documents are left. Click **Review**.",
        "Click each document in the list to open it.",
        "If it's still right, click **Reviewed, no changes** in its **Details** tab. If not, write a revision.",
      ] },
      { p: "Each document's step ticks itself when it's marked reviewed or a new version is published. The review finishes when every step is done." },
    ],
    good: [
      "It covers every document published in the company when it starts, one step per document, each for that document's owner.",
      "It runs once a year. A second start in the same year is refused.",
      "Owners can't tick a review step by hand. If a document no longer applies, archiving or retiring it ticks its step too.",
    ],
    related: ["reviewed-no-changes", "start-workflow", "my-tasks"],
    keys: "annual policy review yearly review all documents owners compliance audit workflow attest",
  },
  {
    id: "start-workflow", topic: "workflows", title: "Start a workflow by hand", applies: "Company admins and managers", updated: UPD,
    summary: "Most workflows start by themselves, like onboarding when someone is added. You can also start one yourself from the Workflows page, for example a custom checklist or this year's policy review.",
    body: [
      { ol: [
        "Open **Workflows** from the menu on the left.",
        "Click **+ Start a workflow**.",
        "Pick the **Company**, if you're in more than one, and the **Workflow**.",
        "If it's about a person, pick them under **Person**.",
        "Click **Start**.",
      ] },
      { p: "The workflow opens and each step goes to its person's **My tasks**. For a workflow on a person, due dates count from their start date on the Team page, or their last day for offboarding. Without one, and for company workflows, they count from today." },
      { h: "Stop one" },
      { p: "Open the workflow and click **Cancel workflow**. The steps stay in the record." },
    ],
    good: [
      "Company admins start any workflow. A manager can start a person workflow for someone they manage.",
      "A person can only have one of each person workflow open at a time.",
      "Company admins build their own workflows in **Settings**, under **Workflows**, with **+ Person workflow** or **+ Company workflow**.",
    ],
    related: ["onboarding", "policy-review", "my-tasks"],
    keys: "start workflow checklist manual custom template run begin launch cancel",
  },

  // ------------------------------------------------------------------ TechHub
  {
    id: "add-client", topic: "techhub", title: "Add a client and its people", applies: "TechHub editors and company admins", updated: UPD, video: "add-client",
    summary: "Each client is one record with its services, sites, contacts and the people who sign in to its computers. Add the client, then fill in its People tab.",
    body: [
      { h: "Add the client" },
      { ol: [
        "In TechHub, open **Clients** and click **New client**.",
        "Enter the **Name**. DocVault suggests a **Code** from the initials. Change it if you like: 2 to 8 capital letters or digits, unique in the company.",
        "Pick the **Status** and tick the **Services** the client gets from you.",
        "Click **Add client**. The client's page opens, and you're its account owner.",
      ] },
      { p: "Pick **Onboarding** as the status and the **New client setup** checklist starts by itself on the client's **Workflows** tab. Some steps tick themselves, such as adding the main site and primary contact." },
      { h: "Add sites and contacts" },
      { ol: [
        "Open **People**, then **Sites**, and click **+ Add site**. Enter the name and address, tick **Main site** for the main office, and click **Save**.",
        "Open **Contacts** and click **+ Add contact**. Enter the name, **Role / title**, **Email** and phone, and pick their **Site**.",
        "Tick **Primary contact** for the person you call first, and **Signs agreements** if they do. Set an **Escalation** order if you use one, then click **Save**.",
      ] },
      { h: "Users" },
      { p: "**Users** are the people who sign in to the client's computers, not the people you call. They come in from your PSA/RMM sync, from the last signed-in user on each device. You can also click **+ Add user**. **Make a contact** copies a user into Contacts." },
    ],
    good: [
      "One record per company. A client served by two of your companies gets a record in each.",
      "A client has one primary contact. Ticking it on someone else moves it.",
      "Missing a service? Company admins add it to the services list, and every client reads the same description.",
      "Once you've checked the details, click **Mark verified**. Clients not verified in 6 months are flagged.",
    ],
    related: ["client-m365", "agreements", "roles"],
    keys: "new client customer company contact site location office user primary escalation onboarding services code",
  },
  {
    id: "client-m365", topic: "techhub", title: "Connect a client's Microsoft 365 tenant", applies: "TechHub editors and company admins", updated: UPD,
    summary: "Connect a client's Microsoft 365 from its page in TechHub. The client's admin approves your company's app once, and DocVault then reads the tenant's licenses, users and security settings every day. It's read only.",
    body: [
      { h: "Before you start" },
      { p: "A company admin sets up your company's app once in **Settings**, then **Integrations**, then **Microsoft 365**. See **Connect Microsoft 365**. Until then, the client's Microsoft 365 tab says it isn't set up yet." },
      { h: "Connect the tenant" },
      { ol: [
        "Open the client, then **Technology**, then **Microsoft 365**, and click **Connect a tenant**.",
        "Pick the **Cloud**: **Commercial** or **GCC**. A client with both connects each one.",
        "Enter the **Tenant domain**, for example contoso.onmicrosoft.com. It's optional, but you need it to use **Check approval** later.",
        "Click **Open the approval page**. Microsoft's page opens in a new tab.",
      ] },
      { h: "What the client's admin approves" },
      { p: "An admin of the client's Microsoft 365 signs in and sees your company's app, by the name you gave it when you registered it. They click **Accept**. The app has only these Microsoft Graph read permissions:" },
      { ul: [
        "**Organization.Read.All**: the tenant's name, domains and licenses bought.",
        "**User.Read.All**: users and which licenses each one has.",
        "**SecurityEvents.Read.All**: Secure Score.",
        "**UserAuthenticationMethod.Read.All**: who has a second sign-in step set up.",
        "**Policy.Read.All**: security defaults and Conditional Access policies, to see if legacy sign-in is blocked.",
      ] },
      { p: "Can't approve it yourself? Click **Copy the link** on the card and send it to their admin. The card shows **Waiting for approval**. Once they've accepted, click **Check approval**. If you have admin rights in their tenant, approve it yourself: you come straight back to DocVault and the tenant is read right away." },
      { h: "What you see" },
      { ul: [
        "Each license with **Bought**, **Assigned**, **Unused** and **Renews**. Click **Who has it** for names and emails.",
        "**Worth a look** lists unused licenses, people with a license who can't sign in, and people with both a commercial and a GCC license.",
        "The client's **Users** tab shows each person's licenses.",
        "DocVault reads the tenant again every day. Click **Sync now** to read it now.",
      ] },
    ],
    good: [
      "DocVault never changes anything in the tenant and never reads mail or files.",
      "**Disconnect** makes DocVault forget the tenant's licenses. To remove access completely, the client's admin also deletes the app under Enterprise applications.",
      "ReviewHub uses Secure Score, multi-factor coverage and legacy sign-in. If a client approved before those permissions were added, ReviewHub shows **Needs approval** and those measures go to Tech Prep until the client's admin approves again.",
      "Viewers and Accounting see the tab but can't connect, sync or disconnect. GCC High isn't supported.",
    ],
    related: ["connect-m365", "add-client", "review-evidence"],
    keys: "microsoft 365 m365 office tenant consent approve admin approval licenses gcc graph secure score mfa entra",
  },
  {
    id: "agreements", topic: "techhub", title: "Agreements and renewal reminders", applies: "TechHub editors, Accounting and company admins", updated: UPD,
    summary: "Each client agreement keeps its dates, renewal terms and signed PDF together, with addenda and rate changes under it. DocVault works out the last day to give notice and reminds the right people before it.",
    body: [
      { h: "Add an agreement" },
      { ol: [
        "Open the client, click the **Agreements** tab, then **Add agreement**.",
        "Pick the **Type** and check the **Title** and **Status**.",
        "Enter the **Signed** and **Effective** dates, the **Term** in months and the **End date**. Leave the end date empty and it fills in from the effective date and term.",
        "Under **Renewal**, pick **Renews automatically**, **Needs a signed renewal** or **Doesn't renew**. Set **Renews for** and **Notice** (days before the end). The form shows the **Notice by** date.",
        "Click **Add agreement**.",
      ] },
      { p: "An agreement such as a BAA can end with the main agreement. Set **Ends** to **With another agreement** and pick it. It has no reminders of its own." },
      { h: "Add the signed PDF" },
      { ol: [
        "On the agreement, click **+ Add signed PDF**.",
        "Choose the PDF (up to 25 MB), pick **What it is**, and fill in the **Title**, **Signed** date and **Signed with**.",
        "Click **Add file**. Addenda, supplements and rate changes go under it the same way with **+ Add to this agreement**.",
      ] },
      { p: "Signed PDFs are kept exactly as uploaded, with their fingerprint recorded. They can't be replaced. If one was added by mistake, an admin removes it and gives a reason." },
      { h: "Renewal reminders" },
      { p: "DocVault emails the client's account owner, company admins and Accounting 90, 60, 30 and 7 days before the last day to give notice, and on the day. When no notice applies, it counts to the end date. Only active agreements get reminders. Agreements due within 90 days also show in **My tasks** and in a banner on the client." },
      { p: "For anything else with a date, such as a price increase, click **+ Reminder**, pick the date under **On**, fill in **Remind about** and click **Add reminder**. On the day, the account owner, the person who added it, Accounting and admins get an email, and it stays in My tasks until someone clicks **Done**." },
      { h: "Who sees what" },
      { ul: [
        "Everyone with TechHub sees agreements, dates and the list of signed files.",
        "Editors and admins add and change agreements and add signed PDFs.",
        "Dollar values are for admins and Accounting only, marked **Admins & Accounting**. Reminder emails never include them.",
        "**Accounting** changes dollar values with **Edit values** and a rate change's **Change amount**, opens signed PDFs and adds reminders. It can't change anything else.",
        "Signed PDFs show prices, so only editors, Accounting and admins open them. Each opening is in the client's Activity.",
      ] },
    ],
    good: [
      "**Agreements** in the TechHub menu lists every agreement, ordered by the next date that needs action.",
      "An **Agreement renewal** checklist starts by itself on the client's **Workflows** tab 90 days before the notice date.",
      "Only admins can delete an agreement, and only one with no signed files. Otherwise mark it **Ended**.",
      "If your company has AI on, **Read with AI** on a signed agreement suggests its dates and terms. Nothing is saved until you tick the values and click **Use checked values**.",
    ],
    related: ["add-client", "roles", "my-tasks"],
    keys: "contract msa baa sow renewal notice auto-renew end date signed pdf addendum rate change price increase accounting reminder",
  },
  // ------------------------------------------------------------------ ReviewHub
  {
    id: "quarterly-review", topic: "reviewhub", title: "Run a quarterly review, start to finish", applies: "ReviewHub editors", updated: UPD, video: "quarterly-review",
    summary: "A review moves through five stages: Intake, Prepare, Review, Close out and Hold. Book the meeting, rate the scorecard and build the roadmap, pass three gates, then meet the client and give them their page.",
    body: [
      { h: "Start and book it" },
      { ol: [
        "In ReviewHub, open **Reviews** and click **Start a review**.",
        "Pick the **Client (from TechHub)**, check the **Quarter** (it looks like FY27-Q1) and click **Start review**.",
        "On **Meeting & people**, set the **Meeting**, the **Owner (account manager)**, the **Engineer (ratings)** and the **Decision-maker attending**, then click **Save**.",
      ] },
      { h: "Prepare" },
      { ol: [
        "On the **Scorecard**, click **Measures** on an area, fill in what's missing and click **Save measures**. Much of it is already filled in. See **Where the evidence comes from**.",
        "The engineer answers **Tech Prep** and clicks **Save**.",
        "On **Risks**, click **Add a risk** for each one that matters. Every red or yellow needs a recommendation.",
        "On **Roadmap**, click **Add** under NOW, NEXT or LATER for each item, and say what investment it needs.",
      ] },
      { h: "Pass the three gates" },
      { ul: [
        "**Readiness**: no more than two areas **Incomplete**.",
        "**No-sell**: at least a third of roadmap items need no investment.",
        "**Pitch check**: on **Client page**, pick someone under **Ask a colleague** and click **Ask**. They answer three questions, confirm they read the whole page and click **Record the Pitch check**.",
      ] },
      { p: "Click **Move to Prepare**, then **Move to Review**. Moving to Review needs the meeting booked and all three gates passed." },
      { h: "The meeting and after" },
      { ol: [
        "Present from **Client page** with **Open full screen**.",
        "Afterwards, write the business pulse on **Pulse notes**. They never appear on the client page.",
        "On **Commitments**, click **Add a commitment** for each one, with an owner and a **Due** date.",
        "On **Client page**, click **Download**, send the file with a MyDocUploads secure link, then click **Mark as given to the client**.",
        "Move it to **Close out**, then **Hold**. Hold needs the page given and an owner and date on every commitment.",
      ] },
    ],
    good: [
      "After a client's first review, the next quarter's review starts itself.",
      "Steps show up in **My tasks**: booking the meeting, getting ready, the Pitch check, giving the client the page and finishing commitments. Company admins choose which reminders run in **Settings**, under **ReviewHub**, then **Reminders and alerts**.",
      "The Pitch check is done by a company admin or ReviewHub editor who isn't the review's owner or engineer.",
      "Each commitment on your side goes to its owner's My tasks. A week late, company admins are told.",
    ],
    related: ["review-evidence", "add-client", "my-tasks"],
    keys: "tbr qbr business review quarterly scorecard roadmap risks commitments gates pitch check client page meeting stages",
  },
  {
    id: "quarterly-review-walkthrough", topic: "reviewhub", title: "Run a quarterly review: the full walkthrough", applies: "Company admins", updated: UPD, admin: true,
    videos: ["qr-walkthrough-1", "qr-walkthrough-2", "qr-walkthrough-3"],
    summary: "Every step of a quarterly review in ReviewHub, from the settings to Hold, recorded with a test client. Watch the parts in order the first time, then jump to a chapter when you need it.",
    body: [],
    good: [
      "The short video in **Run a quarterly review, start to finish** is the 39-second version for editors.",
      "Each part has captions on screen. Click a chapter to jump to it.",
    ],
    related: ["quarterly-review", "review-evidence", "client-page", "rating-definitions"],
    keys: "tbr qbr business review quarterly walkthrough training admin settings rating definitions reminders evidence tech prep scorecard risks roadmap gates pitch check client page pulse commitments close out hold",
  },
  {
    id: "review-evidence", topic: "reviewhub", title: "Where the evidence comes from", applies: "ReviewHub editors", updated: UPD,
    summary: "Most scorecard figures fill themselves. DocVault pulls some from your systems, reads others from reports you upload, and asks the engineer and owner for the rest. Each figure shows where it came from.",
    body: [
      { h: "Pulled automatically" },
      { ul: [
        "**TechHub records**: devices past planned life, workstations on an unsupported Windows, and servers past or near end of support.",
        "**Microsoft 365**: license waste, Secure Score, multi-factor coverage and whether legacy sign-in is blocked. The client's tenant has to be connected in TechHub.",
        "**Public DNS**: DMARC and SPF on the client's email domains.",
        "**Atera**: the quarter's tickets, checked to make sure they're all this client's. The client has to be matched to an Atera customer in **Settings**, under **Integrations**.",
        "**Last review**: whether there's a current roadmap and how many of last quarter's commitments were met.",
      ] },
      { p: "DocVault pulls every morning from Intake until the meeting. To pull now, click **Pull now** on the **Evidence** tab or on the scorecard's evidence card. Pulls are read only and never change anything in your tools." },
      { h: "From uploads" },
      { ol: [
        "Open the **Evidence** tab. Under **Uploads**, click **Upload** on the report's line, or drop the file on the box.",
        "**Atera SLA report** (Excel, run with the quarter's dates) gives first-response SLA. **Atera Auditor patch export** (Excel, this client only) gives critical patch compliance.",
        "**Huntress Managed Learning** (PDF, one a month) gives training completion. **Huntress Managed Phishing** (PDF) feeds a check, and a **Backup report** (optional, any format) is kept with the review.",
      ] },
      { p: "DocVault reads the number out of each report and keeps the file with the review. **Remove** takes its figures back out." },
      { h: "Tech Prep and the owner" },
      { p: "What no system can tell us goes on **Tech Prep** for the engineer: service, backup and recovery, security tools, vulnerabilities and Microsoft 365 data governance. The compliance questions are for the owner, under **For the owner**." },
    ],
    good: [
      "The small label under each measure says where its figure came from. A typed figure wins, but changing one that was found asks you to say why, and the label then reads **Changed**.",
      "**Checks** on the Evidence tab show **Pass**, **Flag** or **Fail**. A flag needs a look; a fail has to be fixed or explained before the meeting.",
      "Under **Sources**, each one shows **Connected**, **Needs approval**, **Not set up** or **Didn't answer**, with what to do.",
      "Reports can be up to 5 MB. Export the quarter only.",
    ],
    related: ["quarterly-review", "client-m365", "connect-m365"],
    keys: "evidence pull upload atera huntress sla patch auditor training phishing dmarc dns secure score mfa tech prep checks source",
  },

  // ------------------------------------------------------------------ VendorHub
  {
    id: "vendor-rules", topic: "vendorhub", title: "Evidence and review rules", applies: "Company admins", updated: UPD,
    summary: "The rules say what evidence a vendor needs because of the data it touches, how long that evidence counts, and how often each vendor is reviewed. Every vendor's evidence state and risk rating come from these rules, so a change applies to every vendor at once.",
    body: [
      { h: "Find the rules" },
      { ol: [
        "Go to **Settings**, open the **VendorHub** tab, then **Evidence and review rules**. You can also click the **rules** link under **Evidence** on a vendor's page.",
        "If you work for more than one company, pick the company at the top. Each company has its own rules.",
      ] },
      { h: "Evidence by data type" },
      { p: "There's a row for each kind of data: **PHI**, **PII**, **Payment** and **CUI**. A vendor touching that data needs one of the ticked kinds of evidence, no older than the months shown." },
      { ol: [
        "Tick **Needs evidence** to add a rule for that data, or untick it to drop the rule.",
        "Tick each kind of evidence that counts, such as **SOC 2 Type II report**, **ISO 27001 certificate** or **Security questionnaire**. Pick at least one.",
        "Set **No older than** to the number of months it counts for, from 1 to 60.",
      ] },
      { p: "A report counts from the end of the period it covers. A certificate counts until it expires. A questionnaire or penetration test counts from the day it was answered." },
      { h: "Reviews and warnings" },
      { ol: [
        "Under **High criticality**, **Medium criticality** and **Low criticality**, set **Review every** to how many months apart reviews are, from 1 to 60.",
        "Set **Running out** to how many days ahead evidence turns amber and the responsible person gets a task, from 7 to 180.",
        "Click **Save changes**, or **Undo** to drop your edits. Nothing changes for anyone until you save.",
      ] },
    ],
    good: [
      "The starting rules: PHI and PII need a SOC 2 report, an ISO 27001 certificate or a security questionnaire (12 months for PHI, 24 for PII); Payment needs a PCI attestation of compliance; CUI needs a FedRAMP authorization. Reviews are every 12, 24 or 36 months by criticality, and warnings start 60 days ahead.",
      "**Put back the starting rules** fills in those starting values. Click **Save changes** to keep them.",
      "A new vendor gets its first review within a month of being added, whatever the rules say.",
      "Every change to the rules is in the audit log.",
    ],
    related: ["add-vendor", "vendor-findings", "roles"],
    keys: "vendorhub rules requirements evidence soc 2 iso 27001 questionnaire pci fedramp months review frequency criticality warning running out amber",
  },
  {
    id: "vendor-findings", topic: "vendorhub", title: "Findings and vendor reviews", applies: "VendorHub editors and company admins", updated: UPD,
    summary: "A finding is a gap you spot in a vendor's reports or answers, like a SOC 2 exception or MFA that isn't enforced. Open findings raise the vendor's risk. A review is your regular check of the vendor; recording it sets the date of the next one.",
    body: [
      { h: "Add a finding" },
      { ol: [
        "Open the vendor's page and, under **Findings**, click **+ Add a finding**.",
        "In **Finding**, say what the gap is, for example that MFA isn't enforced for staff by default.",
        "Pick the **Severity**: **High**, **Medium** or **Low**. Add **Notes** if you like, then click **Add**.",
      ] },
      { p: "An open high finding makes the vendor's risk High. Any other open finding makes it at least Medium. When the vendor fixes it, click **Close** on the finding. If it comes back, click **Reopen**." },
      { h: "Record a review" },
      { p: "About 30 days before a review is due, the responsible person gets a task in **My tasks** to review the vendor. On the Vendors list, **Review due** shows every vendor with a review coming up, overdue or not done yet." },
      { ol: [
        "Open the vendor and click **Record a review** at the top. The form lists any evidence that's still missing or running out.",
        "Check the evidence is current and read any exceptions in their reports. Look at open findings. Click **Edit** if what they touch or how critical they are has changed.",
        "Write what you checked and what you found, then click **Record the review**.",
      ] },
      { p: "The review is dated today and keeps the risk rating at that moment. It shows under **Review** on the vendor's page, and the next review date moves on by the months in your rules." },
    ],
    good: [
      "Risk is worked out from the rules, never typed in. The **Risk** box on the vendor's page lists why, and hovering a rating on the Vendors list shows the same reasons.",
      "An overdue review makes a high-criticality vendor's risk High, and any other vendor's Medium.",
      "Tasks go to the vendor's responsible person, or to company admins when nobody is set. Company admins change who's responsible with **Edit**.",
      "Closed findings and past reviews stay on the vendor's page, and every finding and review is in the audit log.",
    ],
    related: ["vendor-rules", "add-vendor", "my-tasks"],
    keys: "vendorhub finding gap exception soc 2 severity risk review annual check overdue record responsible close reopen",
  },
  // ------------------------------------------------------------------ Integrations
  {
    id: "connect-atera", topic: "integrations", title: "Connect Atera", applies: "Company admins with TechHub", updated: UPD,
    summary: "Connect Atera once with an API key and TechHub keeps each client's contacts, users and devices current without retyping. You match each Atera customer to a TechHub client once. DocVault only reads from Atera; it never changes anything there.",
    body: [
      { h: "Get the key from Atera" },
      { p: "In Atera, go to **Admin**, then **Data management**, then **API**, and copy the API key." },
      { h: "Connect it" },
      { ol: [
        "In DocVault, go to **Settings**, then **Integrations**, and click **+ Add integration**.",
        "Click **Connect** on Atera. If you have more than one company, pick the company at the top, then click **Connect**.",
        "Paste the key in the **Atera API key** box and click **Connect**. DocVault checks the key, then reads Atera's customer list.",
      ] },
      { h: "Match your customers" },
      { ol: [
        "Under **Matching**, each Atera customer that isn't matched yet has a list of your TechHub clients. A likely match shows as **Suggested**.",
        "Pick the client and click **Match**. Click **New client** to add the customer as a new TechHub client with its own short code, or **Ignore** to leave it out.",
        "Change **Showing** to **Everything** to see matched and ignored customers too. **Change** or **Undo** puts one back to be matched again.",
      ] },
      { p: "Customers that aren't matched are never brought in." },
      { h: "Choose what syncs" },
      { p: "Under **What syncs**, choose who becomes a contact (**Main contact only**, **Everyone** or **Nobody**) and turn **Users**, **Devices**, **Main site and phone** and **Network suggestions** on or off. **How often** sets the schedule: every 3, 6, 12 or 24 hours. Click **Sync now** to sync straight away; **History** shows each run and what changed." },
    ],
    good: [
      "The key is stored encrypted and never shown again; the page only shows how it ends. To use a new one, click **More**, then **Change key**.",
      "Fields someone checked by hand are never overwritten, and nothing is deleted. A device gone from Atera stays until a person removes it.",
      "Disconnecting Atera (**More**, then **Disconnect**) keeps the clients, contacts and devices it brought in.",
      "Atera's customer list is read again on each sync. Click **Refresh list** to read it now, for example after adding a customer in Atera.",
    ],
    related: ["connect-m365", "roles"],
    keys: "atera rmm psa integration api key sync devices contacts users match customers techhub clienthub",
  },
  {
    id: "m365-secret", topic: "integrations", title: "Renew the Microsoft 365 secret", applies: "Company admins with TechHub", updated: UPD,
    summary: "The secret your Microsoft 365 app uses lasts up to 24 months. Before it runs out, make a new one in Entra and paste it into DocVault. The old one keeps working until the new one is saved, and your clients don't need to approve anything again.",
    body: [
      { h: "How you'll know" },
      { ul: [
        "Company admins get an email 60, 30, 14, 7 and 1 days before the secret expires, on the day, and 1 and 7 days after.",
        "From 60 days out, a banner at the top of the **Microsoft 365** tab in **Integrations** says when it expires, and the app shows **Secret expires in** and the number of days.",
        "Once it has expired, licenses stop updating for every client tenant until a new secret is saved.",
      ] },
      { h: "Make a new secret in Entra" },
      { ol: [
        "In the Microsoft Entra admin center, open **Entra ID**, then **App registrations**, and open your DocVault app.",
        "Under **Certificates & secrets**, create a new client secret for 24 months.",
        "Copy its **Value** right away; Microsoft shows it only once. Note the expiry date Entra shows next to it.",
      ] },
      { h: "Paste it in DocVault" },
      { ol: [
        "Go to **Settings**, then **Integrations**, then **Microsoft 365**. If you have more than one company, pick the company at the top.",
        "Click **Replace secret**.",
        "Paste the secret's Value in **New client secret value** and set **Expires** to the date Entra showed.",
        "Click **Test and save**. DocVault signs in to Microsoft with the new secret before it saves it.",
        "Once it's saved, delete the old secret in Entra.",
      ] },
    ],
    good: [
      "Paste the secret's **Value**, not its **Secret ID**. DocVault tells you if it looks like the ID.",
      "The secret is stored encrypted. Nobody can read it back, not even admins; you can only replace it.",
      "The emails follow the **SSL and domain expiry (TechHub)** email setting, so leave that on if you're the one who renews it.",
      "**More**, then **Edit app details** also takes a new secret. Leave the secret blank there to keep the current one.",
    ],
    related: ["connect-m365", "client-consent"],
    keys: "microsoft 365 m365 secret expire expired expiry renew replace client secret entra certificates app registration licenses stopped updating",
  },
  {
    id: "client-consent", topic: "integrations", title: "What your clients are approving", applies: "Company admins and TechHub editors", updated: UPD,
    summary: "When you connect a client's Microsoft 365 tenant, an admin of that tenant approves your company's DocVault app once. It can only read: licenses, users, Secure Score, MFA methods and sign-in policies. It never reads mail or files and never changes anything, and the client can remove it at any time.",
    body: [
      { h: "What the admin sees" },
      { ol: [
        "You send them the approval link from the client's page in TechHub: **Technology**, then **Microsoft 365**. Use **Copy the link**, or **Open the approval page** if you have admin rights there.",
        "The link opens Microsoft's own approval page, which shows your app's name. That's why the app is named after your company.",
        "They sign in with an admin account for their Microsoft 365 and click **Accept**. That approves the app for their whole tenant.",
      ] },
      { h: "What it can read" },
      { p: "Every permission is a read-only Microsoft Graph **Application** permission:" },
      { ul: [
        "**Organization.Read.All**: the tenant's name and domains, and the licenses bought, assigned and their renewal dates.",
        "**User.Read.All**: each person's name, sign-in name and email, whether they can sign in, and which licenses they hold.",
        "**SecurityEvents.Read.All**: the tenant's Microsoft Secure Score.",
        "**UserAuthenticationMethod.Read.All**: which licensed people have a second sign-in method, to work out MFA coverage.",
        "**Policy.Read.All**: whether security defaults are on and whether a Conditional Access policy blocks legacy sign-in.",
      ] },
      { p: "Licenses and who holds each one show on the client's **Microsoft 365** tab, read again every day. Secure Score, MFA coverage and legacy sign-in go into the client's quarterly review in ReviewHub." },
      { h: "What it never does" },
      { ul: [
        "It doesn't read mailboxes, files or sign-in logs.",
        "It can't send mail or change anything in the tenant.",
      ] },
      { h: "Removing it" },
      { ol: [
        "In DocVault, click **Disconnect** on the tenant's card on the client's **Microsoft 365** tab. DocVault forgets its licenses.",
        "To remove the app's access completely, the client's admin also deletes it under **Enterprise applications** in their Microsoft Entra admin center.",
      ] },
    ],
    good: [
      "A client with both a commercial and a GCC tenant approves each one separately.",
      "If the client's admin approves it on their own, click **Check approval** on the tenant's card to finish connecting. This needs the tenant's domain, entered when you connected it.",
      "If a client removes the app without telling you, the tenant shows **Approval removed** under **Needs attention** in **Settings**, **Integrations**, **Microsoft 365**. Send them the approval link again to reconnect.",
    ],
    related: ["connect-m365", "m365-secret"],
    keys: "microsoft 365 m365 consent admin consent approve accept permissions graph read only privacy enterprise applications remove revoke gcc tenant client",
  },
  // ------------------------------------------------------------------ third batch (2026-10-30)

  // ------------------------------------------------------------------ Documents and approvals
  {
    id: "download-copy", topic: "docs", title: "Download or print a controlled copy", applies: "Everyone", updated: UPD,
    summary: "You can download the current approved version of any document you can read, as a PDF copy. Every page of the copy is marked with the document number, a note that it's uncontrolled once printed or downloaded, and who downloaded it and when. Every download goes in the audit log.",
    body: [
      { h: "Download the PDF copy" },
      { ol: [
        "Open the document. It opens at its current approved version.",
        "Click **PDF** in the bar above the document. It's grayed out until the PDF copy is ready.",
        "The file downloads. To print it, open it and print it as you would any PDF.",
      ] },
      { p: "Every page of the download gets a footer with the classification, the document number and version, a note that it's uncontrolled when printed or downloaded, and your name with the date and time. The time follows the **Time zone** in **My profile**." },
      { h: "Forms" },
      { p: "For a form, readers also get **Fillable form** in the same bar. It's the original file, so you can fill it in. It doesn't get the footer, which keeps the form fields working." },
      { h: "Originals, drafts and older versions" },
      { p: "Authors, reviewers, approvers and company admins can also:" },
      { ul: [
        "Click **Original** to download the file as it was uploaded. A PDF original gets the footer; Word, Excel and other files come as they are.",
        "Switch to the version in review with the buttons above the document, then click **PDF**. Its pages are marked DRAFT - NOT APPROVED, with DRAFT across each page.",
        "Open the **Revisions** tab and click **PDF** next to an older version. Its pages are marked SUPERSEDED, so nobody mistakes it for the current one. A version approved for a later date is marked NOT YET IN EFFECT.",
      ] },
    ],
    good: [
      "Readers only ever download the current approved version, as a PDF copy. The original file is only for forms.",
      "Each download is recorded in the audit log, with whether it was the PDF copy or the original file.",
      "Videos have no PDF copy. They play on the document's page.",
      "Archived and retired documents, and Restricted ones you aren't named on, are hidden from readers, so they can't be downloaded.",
    ],
    related: ["find", "document-settings", "audit-log"],
    keys: "download print printing pdf copy controlled copy uncontrolled stamp footer watermark original fillable form superseded draft offline",
  },
  {
    id: "import-documents", topic: "docs", title: "Import existing documents", applies: "Authors", updated: UPD,
    summary: "Bring many files in at once. Each one becomes a draft with its own document number, just as if you'd used New document. Nothing is submitted or published until you send each one for approval.",
    body: [
      { h: "Add the files" },
      { ol: [
        "Click **Import documents** in the menu on the left.",
        "Drop the files on **Drop the files here**, or click it and select them all at once. Each file can be up to 100 MB. How-Tos and SOPs can also be a video, up to 1 GB.",
        "DocVault makes a title from each file name and suggests a type. Check the title and **Type**, pick the **Area** and check the **Company** on each row.",
      ] },
      { h: "Check the list" },
      { p: "The **Status** column says what each row needs:" },
      { ul: [
        "**Ready**: it will be imported.",
        "**Needs a title**, **Pick a type** or **Pick an area**: fill in that column and the row is ready.",
        "**Already in the library**: a document with the same title is already in that company, so the row is unticked and skipped. Tick it to import it anyway.",
      ] },
      { h: "Import them" },
      { ol: [
        "Untick any row you don't want.",
        "Click the import button, for example **Import 12 as drafts**. The number is how many rows are ticked.",
        "As each file finishes, its new document number shows under **Status**. Click it to open the draft.",
      ] },
      { p: "The drafts are yours, and they show under **My drafts**. Open each one, check its details and click **Submit for approval** when it's ready." },
      { h: "Fill in everything at once" },
      { p: "For a big batch, add a list named **docvault-import.json** with the files. For each file it can give the title, type, area, company, a description and notes. Files that aren't on it show **not on the list** under the file name." },
    ],
    good: [
      "If a row fails, its message says why. Fix it and click the import button again to retry it.",
      "Imported drafts start as **Internal**, with no retention set. Change that on each document's **Details** tab before you submit it.",
      "You only see **Import documents** if you're an author, and you can only import into companies where you're an author.",
      "**Clear the list** starts over. It doesn't remove drafts that were already imported.",
    ],
    related: ["new-document", "document-settings", "find"],
    keys: "import bulk upload many files migrate migration existing documents drafts batch json manifest",
  },
  {
    id: "document-settings", topic: "docs", title: "Classification, visibility, retention and acknowledgment settings", applies: "Authors and company admins", updated: UPD,
    summary: "These settings describe a document and who has to read it. They're not part of its content, so changing them doesn't need a new revision. Find them in the document's Details tab. Every change goes in the audit log.",
    body: [
      { h: "Change classification, visibility, retention or owner" },
      { ol: [
        "Open the document and click the **Details** tab.",
        "Click **Edit classification, visibility, retention or owner**.",
        "Pick the new values and click **Save**.",
      ] },
      { p: "The change applies straight away." },
      { h: "What each one means" },
      { ul: [
        "**Classification**: **Public**, **Internal**, **Confidential** or **Restricted**. Anything other than Internal shows at the top of the document, and every downloaded PDF copy prints it on each page.",
        "**Restricted** also limits who can read it: its authors, reviewers, approvers and admins, plus the people named under **Who else can read it**. Pick a person there and click **Add**, or click **Remove** next to a name.",
        "**Visibility**: **Internal only**, **Client shared** or **Public**. Client shared and Public don't change anything yet. Until client access arrives, nobody outside your companies can see a document.",
        "**Retention**: how long the document is kept, such as **While current, then 3 years**, **7 years after it is retired** or **Permanent**. It's a label for your records, shown in **Details** and when you retire the document.",
        "**Owner**: the person who gets review reminders and suggested changes. It must be an author, approver or admin of the company.",
      ] },
      { h: "Ask readers to acknowledge it" },
      { ol: [
        "In the **Details** tab, under **Acknowledgment**, tick **Readers must read and acknowledge it**.",
        "Under **Who must acknowledge it**, leave **Everyone who can read it**, or pick **Only these roles** and click each role to ask.",
        "Leave **Ask again on each new version** ticked to ask again on every major or minor version. Untick it and one acknowledgment counts for every version.",
      ] },
      { p: "Each change saves as soon as you click it. The people asked get the document in **My tasks**, and new people in those roles are asked automatically, with 7 days to confirm." },
      { p: "Once it's published, the **Read by** tab shows who has acknowledged the current version and who hasn't. Click **Export for an audit** to download the list as a CSV file." },
    ],
    good: [
      "Readers see these values in **Details** but can't change them.",
      "Archived and retired documents can't be changed.",
      "A revision, such as a typo fix, never asks people to acknowledge again.",
      "Framework tags are edited on their own: click **Edit** next to **Frameworks** in **Details**.",
    ],
    related: ["acknowledge", "new-document", "archive-retire"],
    keys: "classification confidential restricted internal public visibility retention owner change owner acknowledgment acknowledgement required reading read by export metadata details settings",
  },
  {
    id: "archive-retire", topic: "docs", title: "Archive or retire a document", applies: "Company admins", updated: UPD,
    summary: "Archive a document you might need again: readers stop seeing it, and an admin can restore it. Retire a document at the end of its life: it becomes read-only for good and can't be brought back. Only company admins do either.",
    body: [
      { h: "Archive a document" },
      { ol: [
        "Open the document and click **Archive** at the top.",
        "In **Why**, say why if you like, for example which document replaced it. It shows on the document.",
        "Click **Archive**.",
      ] },
      { p: "Readers can't see it any more, and review reminders stop. Authors, reviewers, approvers and admins still find it, with its full history, under **Show archived** in the **Library**. If it was checked out, the check-out is released." },
      { h: "Bring it back" },
      { p: "Open it from **Show archived** and click **Restore** in the bar at the top. It goes back to how it was, published or draft." },
      { h: "Retire a document" },
      { ol: [
        "Open the document and click **Retire** at the top. On an archived document, **Retire** is in the bar at the top.",
        "In **Why**, say why if you like.",
        "Click **Retire**. This can't be undone.",
      ] },
      { p: "A retired document is read-only for good: hidden from readers, no new versions. DocVault keeps it and its history; its retention rule is a label for your records and nothing is deleted automatically. Only a document that was published can be retired. If you need it again, start a new document." },
      { h: "When you can't" },
      { ul: [
        "You can't archive a document while a revision is waiting for approval. Approve it, request changes, or have the author withdraw it first.",
        "You can't archive or retire a document while an approved version is waiting for its effective date.",
        "**Retire** doesn't show while a new revision is being worked on. Finish or withdraw it first.",
      ] },
    ],
    good: [
      "Not sure? Archive it. You can still retire it later.",
      "If the annual policy review is running, archiving or retiring a document ticks its step.",
      "The weekly digest lists documents you own that nobody has opened in 12 months, so you can decide whether they're still needed.",
      "Archiving, restoring and retiring are recorded in the document's **Activity** and the audit log, with the reason.",
    ],
    related: ["document-settings", "policy-review", "audit-log"],
    keys: "archive archived retire retired restore unarchive delete remove obsolete end of life hide old document show archived",
  },
  // ------------------------------------------------------------------ Workflows
  {
    id: "edit-workflow-template", topic: "workflows", title: "Edit a workflow template", applies: "Company admins", updated: UPD,
    summary: "Each workflow, like onboarding, runs from a template: its steps, who does each one and when it's due. Company admins change templates in Settings, under Workflows. Changes apply to workflows started after you save; ones already running keep their steps.",
    body: [
      { h: "Open the template" },
      { ol: [
        "Go to **Settings**, then **Workflows**.",
        "If you're an admin of more than one company, pick the company at the top. Each company has its own templates.",
        "Find the workflow and click **Edit**.",
      ] },
      { h: "Change how it starts" },
      { ul: [
        "Built-in workflows have a box for starting by themselves, such as **Starts when someone is added to the company** for onboarding or **Starts when a last day is set on the Team page** for offboarding. Untick it and the workflow only starts by hand.",
        "The annual policy review has **Starts every year on the date below** and **Yearly start (month-day)**, for example 10-15.",
        "Untick **In use** to stop using a workflow. It shows as **Retired** and can't be started.",
      ] },
      { h: "Change the steps" },
      { p: "Each row is one step:" },
      { ul: [
        "**Step**: what to do, in a few words (3 to 200 characters).",
        "**Kind**: what sort of step it is. See below.",
        "**Who**: **Company admins**, **The person**, **Their manager**, **The workflow's owner** or **Anyone on it**. Company workflows have no person or manager.",
        "**Due (days)**: days from the start, from -60 to 365. A minus number means before. For a person, the start is their start date, or their last day for offboarding, on the Team page. Otherwise it's the day the workflow starts.",
      ] },
      { p: "Use the arrows to move a step up or down, the **×** to remove it, and **+ Add step** to add one. Click **Save** when you're done, or **Cancel** to leave it as it was." },
      { h: "Kinds of step" },
      { ul: [
        "**Task**: someone ticks it when it's done.",
        "**Read (role's required reading)**: ticks itself when the person acknowledges what their role has to read. Person workflows only.",
        "**Upload**: ticks itself when someone adds the file. The file is kept with the step.",
        "**Approval**: someone answers **Yes** or **Not yet**, with a note.",
        "**Auto check**: DocVault ticks it when it can see the work is done. Instead of **Who**, pick what it checks: **They've signed in and set up two-step**, **They no longer have access to the company** or **They own no documents in the company**. Person workflows only.",
        "**Review each document**: company workflows only. Each published document gets a step for its owner.",
        "**Form**: ticked by hand, like a task.",
      ] },
    ],
    good: [
      "Saving never changes workflows already running. Only new ones use the new steps.",
      "Steps with no title are dropped when you save. A workflow can have up to 60 steps.",
      "**+ Person workflow** and **+ Company workflow** at the top add your own. They start by hand; see **Start a workflow by hand**.",
      "TechHub's client checklists aren't here. Edit them in **Settings**, under **Checklists**.",
    ],
    related: ["start-workflow", "onboarding", "policy-review"],
    keys: "workflow template edit change steps checklist onboarding offboarding policy review due days who kind task read upload approval auto check retire in use",
  },
  // ------------------------------------------------------------------ Account and security
  {
    id: "audit-log", topic: "account", title: "The audit log", applies: "Company admins", updated: UPD,
    summary: "The audit log records every view, download, upload, approval and admin change in the companies you administer. Nobody can edit or delete it. Filter it by person, document, company, period and kind of event, and export it for an auditor.",
    body: [
      { h: "Open it" },
      { ol: [
        "Go to **Settings**. On the **Account** tab, click **Audit log**.",
        "The newest events are at the top. Each row shows **When**, **Who**, **What** and the **Company**. Click a document's name to open it.",
      ] },
      { h: "Narrow it down" },
      { ul: [
        "Type in the search box to find a person, a document title or a document number.",
        "Pick a company, or leave **All companies**.",
        "Pick a period: **Last 7 days**, **Last 30 days**, **Last 90 days** or **Last 365 days**. It starts at 30 days.",
        "Click a button to show one kind of event: **Approvals**, **Uploads** (new documents and files, and other changes to documents such as archiving or a new classification), **Views & downloads** or **Admin changes**. **Everything** shows them all.",
      ] },
      { p: "The page shows 100 events at a time. Click **Show more** for the next ones." },
      { h: "Export it" },
      { p: "Click **Export CSV** to download everything that matches your filters, up to 50,000 rows. The export itself is recorded." },
      { h: "What gets recorded" },
      { ul: [
        "Every time someone opens or downloads a document, and whether it was the PDF copy or the original file.",
        "New documents, uploads, submissions, approvals, requested changes and acknowledgments.",
        "Check-outs, archiving, restoring and retiring, with any reason given.",
        "Changes to a document's classification, visibility, retention, owner, readers and framework tags.",
        "Role changes, removed members and changes to company settings.",
      ] },
    ],
    good: [
      "Only company admins see the audit log, and only for the companies they administer.",
      "Nothing in the log can be changed or removed, by anyone.",
      "Authors, reviewers, approvers and admins see a document's own history in its **Activity** tab. Views and downloads are only in the audit log.",
    ],
    related: ["roles", "download-copy", "add-people"],
    keys: "audit log trail history activity who viewed downloaded changed export csv compliance evidence auditor events filter",
  },

  // ------------------------------------------------------------------ TechHub
  {
    id: "devices-networks", topic: "techhub", title: "Devices, networks and domains", applies: "TechHub editors and company admins", updated: UPD,
    summary: "A client's Technology tab holds its devices under Assets, its internet connections and networks under Networks, and its websites under Domains. DocVault checks each website's certificate and domain every day and reminds you before either runs out. Passwords never go in TechHub.",
    body: [
      { h: "Add a device" },
      { ol: [
        "Open the client, then **Technology**, then **Assets**, and click **Add asset**.",
        "Enter the **Name** and pick the **Type**, **Status** and **Site**.",
        "Fill in what you know: **Manufacturer**, **Model**, **Serial**, **Operating system**, **MAC address** and **IP address**. **Assigned to**, **Purchased** and **Warranty ends** are optional.",
        "Click **Add asset**.",
      ] },
      { p: "If the IP address sits inside one of the client's networks, it's recorded on that network too. Click a device in the list to see its details, then **Edit asset** or **Remove**. To keep the record of a device that's gone, set its **Status** to **Retired** instead. Tick **Show retired** to see those." },
      { h: "Import from a spreadsheet" },
      { ol: [
        "On **Assets**, click **Import assets**, then **Choose a file**. CSV or Excel (.xlsx) with the column names in the first row, up to 2,000 devices. An Atera export works.",
        "Under **1. Match the columns**, pick where each column goes, or **Don't import**. Your choices are saved for this client.",
        "Click **2. Check before anything changes**. **3. What will happen** shows each row as **Add**, **Update**, **Check** or **Skip**.",
        "A **Check** row has a field someone changed by hand that the file disagrees with. Pick **Keep ours** or **Use the file's**.",
        "Click **Import**. The button says how many it adds and updates.",
      ] },
      { p: "Rows are matched to devices by serial, then by name. An import never deletes anything, a blank cell never clears a value, and columns that look like passwords are never imported." },
      { h: "Networks" },
      { ol: [
        "Open **Technology**, then **Networks**.",
        "Under **Internet**, click **Add internet connection**. Enter the **Provider**, pick the **Role** and **Type**, add the speeds, account number and support phone, and click **Add connection**.",
        "Under **LAN networks**, click **Add LAN network**. Enter a **Name** and the **Range**, for example 10.20.1.0/24. **Gateway**, **DNS servers** and the DHCP range are optional. Click **Add LAN network**.",
        "Under **Wi-Fi networks**, click **Add Wi-Fi network**. Enter the **SSID** and **Security**, then pick **Uses a LAN network** or **Has its own range**.",
      ] },
      { p: "Click a network to see its addresses. Type the address, the device and its **Purpose**, then click **Add IP**. DocVault checks every address sits inside the network's range, and marks one that's **In DHCP range** unless it's a **DHCP reservation**." },
      { p: "If Atera is connected, **Found in Atera** suggests networks and public IPs from device addresses. Click **Add network** or **Add to Internet** to check and add one, or **Not a network** or **Not ours** to hide it. Nothing is added until you click." },
      { p: "Firewalls, routers, switches, access points and printers from Assets also show under **Network equipment**. Set each one's **Connects to** and the map under **How it connects** draws itself. **Link a diagram** points to a network diagram already linked on the client's **Documents** tab." },
      { h: "Domains and websites" },
      { ol: [
        "Open **Technology**, then **Domains**, and click **Add domain or website**.",
        "Paste the address people type, such as www.example.com, and click **Add**.",
      ] },
      { p: "DocVault checks the website's SSL certificate and the domain's registration straight away, then every day. The list shows when each was issued or registered, by whom, and when it expires. If the registry doesn't say when a domain expires, click **Type it in** and enter the date." },
      { p: "Dates turn amber within 60 days and red within 30. The client's account owner and company admins get an email 60 and 45 days before a certificate or domain expires, then 30, 14, 7, 3 and 1 days before, on the day, and 1 and 7 days after. It also shows in their **My tasks** from 60 days out." },
    ],
    good: [
      "Passwords never go in TechHub. Paste the 1Password item link in **1Password item for its credentials**, or **1Password item for the Wi-Fi key**, instead. Notes that look like a password are refused.",
      "Everyone with TechHub sees these tabs. Only TechHub editors and company admins add, change or remove anything.",
      "A client's account owner gets a **Warranties ending** task in **My tasks** when a device's warranty ends within 30 days.",
      "Domain emails follow the **SSL and domain expiry (TechHub)** email setting in **Notifications**.",
    ],
    related: ["add-client", "connect-atera", "client-m365"],
    keys: "assets devices computers servers import csv excel spreadsheet networks lan wifi wi-fi ssid vlan ip address dhcp internet isp firewall switch access point domain website ssl certificate expiry registrar 1password techhub clienthub",
  },
  {
    id: "client-checklists", topic: "techhub", title: "Client checklists", applies: "TechHub editors, Accounting and company admins", updated: UPD,
    summary: "Checklists guide the work on a client, like setting up a new client, offboarding one or renewing an agreement. The main ones start by themselves. Each step says who it's for, and some tick themselves when DocVault sees the work is done.",
    body: [
      { h: "When they start" },
      { ul: [
        "**New client setup**: when a client is added with the status **Onboarding**. It's due in 14 days.",
        "**Client offboarding**: when a client's status changes to **Former client**. It's due in 30 days.",
        "**Agreement renewal**: 90 days before an agreement's notice date, or its end date when no notice applies. It's due on that date.",
      ] },
      { h: "Work through one" },
      { ol: [
        "Open the client and click the **Workflows** tab. Each open checklist shows its due date and how many steps are done.",
        "Each step says who it's for: **Account owner**, **Accounting**, **Admin** or **Anyone who edits**.",
        "Tick the box next to a step when it's done. Click it again to untick it.",
        "Use **Go to** on a step, such as **Go to Assets**, to open the tab where the work is done. **Open the form in MyDocUploads** opens a form to fill in.",
      ] },
      { p: "A step marked **Ticks itself when** ticks once DocVault sees the work, for example when a site and a primary contact are recorded. You can't tick those by hand. When every step is done, the checklist moves to **Done**." },
      { h: "Start one by hand" },
      { ol: [
        "On the client's **Workflows** tab, click **Start a checklist**.",
        "Pick the **Checklist**. For an agreement renewal, also pick the **Agreement**. Only active agreements are listed.",
        "Click **Start**.",
      ] },
      { p: "A client can have only one of each checklist open at a time. To stop one, click **Cancel** on it. Its steps stay in the record." },
      { h: "Change the steps" },
      { p: "Company admins change what each checklist holds:" },
      { ol: [
        "Go to **Settings**, open the **TechHub** tab, then **Checklists**.",
        "Click **Edit steps** on a checklist.",
        "Change a step's title, who it's for and its automatic check, or add a MyDocUploads form link. Use the arrows to reorder, **Remove** to drop a step and **+ Add step** to add one.",
        "Click **Save**.",
      ] },
      { p: "Changes apply to checklists started from now on. Ones already open keep their steps." },
    ],
    good: [
      "Steps waiting on you show in **My tasks**. Account owner steps go to the client's account owner, Accounting steps to Accounting, Admin steps to company admins, and Anyone who edits steps to TechHub editors.",
      "Accounting ticks only the steps marked for Accounting. Only company admins tick Admin steps.",
      "The account owner gets an email when someone else ticks a step, and the account owner and whoever started it hear when it's finished. These follow the **Done notices** email setting.",
      "A renewal checklist that was finished or canceled isn't started again for the same deadline. Every start, tick and cancel is in the client's **Activity**.",
    ],
    related: ["add-client", "agreements", "my-tasks"],
    keys: "checklist new client setup onboarding offboarding former client renewal steps tick automatic mydocuploads form template techhub clienthub",
  },
  // ------------------------------------------------------------------ ReviewHub
  {
    id: "client-page", topic: "reviewhub", title: "The client page", applies: "ReviewHub editors", updated: UPD,
    summary: "Each review has a client page: the review laid out for the client, filled from the scorecard, risks, roadmap and commitments. Present from it in the meeting, then download it, send it and mark it given. DocVault keeps a copy of exactly what the client got.",
    body: [
      { h: "Open it" },
      { ol: [
        "Open the review and click the **Client page** tab, or **Client page** at the top.",
        "The preview shows the page as the client will see it. Click **Open full screen** to present it in the meeting.",
      ] },
      { h: "Change the wording" },
      { p: "Health, risks, roadmap, budget and commitments fill themselves from the review. Under **Sections**, each row says what fills it: **The review**, **The review, plus your wording**, **Your wording** or **Starting wording**." },
      { ol: [
        "Click **Edit wording** on a section.",
        "Change the **Small heading**, **Title** and **Opening line**. Where the section has lists or notes, use **Add a line**, **Add a list** or **Add a note**.",
        "Click **Save wording**. The preview updates. **Start over** puts the starting wording back.",
      ] },
      { p: "To leave a section off this review, turn off its switch under **On this page**. A section a company admin has turned off shows **Off for the company**." },
      { h: "Give it to the client" },
      { ol: [
        "After the meeting, click **Download**. The page saves as a file you can open in any browser. To leave a PDF behind, print the page to PDF from your browser.",
        "Send the file with a MyDocUploads secure link.",
        "Click **Mark as given to the client**.",
      ] },
      { p: "**Mark as given to the client** works once the review reaches the meeting stage and the Pitch check has passed. Each copy given is kept and listed under the button. Click one to open it." },
      { h: "Sections for every review" },
      { p: "Company admins choose which sections every review's page has:" },
      { ol: [
        "Go to **Settings**, open the **ReviewHub** tab, then **Client page**.",
        "Turn each section on or off, and use **Up** and **Down** to change the order. Each change saves as you make it.",
      ] },
    ],
    good: [
      "Pulse notes never appear on the client page.",
      "Before the meeting, changing the wording means the Pitch check has to be done again.",
      "A page already given to the client keeps what it showed, even if the review or the rating lines change later.",
      "ReviewHub viewers can see and download the page. Only ReviewHub editors and company admins change it or mark it given.",
    ],
    related: ["quarterly-review", "rating-definitions", "review-evidence"],
    keys: "client page tbr qbr presentation leave-behind download pdf sections wording mark as given deliver pitch check mydocuploads reviewhub",
  },
  {
    id: "rating-definitions", topic: "reviewhub", title: "Rating definitions", applies: "Company admins with ReviewHub", updated: UPD,
    summary: "Rating definitions are the lines that turn each scorecard figure into a color. Your company starts with the standard lines and can move them. The measures themselves stay the same, so quarters still compare fairly.",
    body: [
      { h: "How a color is worked out" },
      { ul: [
        "Each measure is **Healthy**, **Attention** or **Action required**, from the lines on this page.",
        "An area takes its worst color. One red measure makes the whole area red.",
        "A required measure left blank makes the area **Incomplete**. No visibility is not green.",
        "An area marked not in scope for a client shows **Not in scope**. Strategic Planning shows **Developing** on a client's first review.",
      ] },
      { h: "Move a line" },
      { ol: [
        "Go to **Settings**, open the **ReviewHub** tab, then **Rating definitions**. If you work for more than one company, pick the company at the top.",
        "Pick an area, such as **Cybersecurity**. Each measure shows its bands and whether it's **Required** or **Optional**.",
        "Type a new number in a band's box. The band next to it updates to match.",
        "Click **Save changes**, or **Undo** to drop your edits. Nothing changes for anyone until you save.",
      ] },
      { p: "The red line has to stay past where Attention starts, and a percentage is 0 to 100. If a number doesn't fit, it's highlighted and nothing saves until you fix it." },
      { h: "Go back to the standard" },
      { p: "A measure you've changed shows the standard under it, and its area shows how many are changed. Click **Use the standard** to put one back, or **Reset all to the standard** at the top for every measure. Then click **Save changes**." },
    ],
    good: [
      "The standard is ITAssurance's Rating Definitions v1.0.",
      "Measures answered with a word, like yes or no or the DMARC setting, are marked **Fixed**. Only numbers can move.",
      "When you save, every review's colors follow the new lines. A page already given to a client keeps what it showed.",
      "Every change is in the audit log.",
    ],
    related: ["quarterly-review", "review-evidence", "client-page"],
    keys: "rating definitions thresholds lines colors red yellow green healthy attention action required incomplete scorecard measures standard reset reviewhub",
  },
  // ------------------------------------------------------------------ Integrations
  {
    id: "setup-guide-link", topic: "integrations", title: "Link your own setup guide", applies: "Company admins with TechHub", updated: UPD,
    summary: "If your company keeps its own steps for Microsoft 365 or Atera in the Vault, link that document to the integration. A Setup guide button then opens it from Settings, so the steps your team follows are one click away.",
    body: [
      { h: "Link a guide" },
      { ol: [
        "Go to **Settings**, open the **TechHub** tab, then **Integrations**.",
        "Open the **Microsoft 365** tab or your tool's tab, such as Atera. If you have more than one company, pick the company at the top.",
        "Click **Link setup guide**.",
        "Type at least two letters of the document's number or title, then click it in the list.",
      ] },
      { p: "The button changes to **Setup guide**. Click it to open the document in the Vault." },
      { h: "What you can link" },
      { ul: [
        "A document from the same company, or a company-wide one.",
        "Not an archived or retired document.",
        "A document that isn't approved yet can be linked. The list marks it **not approved yet**.",
      ] },
      { h: "Change or remove it" },
      { ol: [
        "Click **More**, then **Change setup guide**.",
        "Pick a different document, or click **Remove link**. **Cancel** closes it without changing anything.",
      ] },
    ],
    good: [
      "Each integration has its own guide, and each company has its own.",
      "The button opens the document's current approved version, so a new revision shows up without linking it again.",
      "Only company admins link, change or remove a guide, and each change is in the audit log.",
    ],
    related: ["connect-m365", "connect-atera"],
    keys: "setup guide link sop procedure document vault integration microsoft 365 m365 atera instructions runbook",
  },
  // ------------------------------------------------------------------ Account and security
  {
    id: "company-branding", topic: "account", title: "Company names and branding", applies: "Company admins", updated: UPD,
    summary: "Each company has a display name, a logo and an accent color. They show in the company picker, across DocVault and in emails. Company admins change them in Settings, under Companies.",
    body: [
      { h: "Change the name" },
      { ol: [
        "Go to **Settings**, then **Companies**. If you administer more than one company, pick it at the top.",
        "Change the **Display name**. It can be 2 to 60 characters.",
        "Click **Save changes**.",
      ] },
      { p: "The company code and document numbers don't change." },
      { h: "Add a logo" },
      { ol: [
        "Under **Branding**, click **Upload logo**, or **Replace** if there's one already.",
        "Pick a PNG, JPEG or WebP file up to 512 KB. A wide logo on a transparent background works best.",
      ] },
      { p: "The logo saves straight away. DocVault also picks an accent color from it; click **Save changes** to keep that color. **Remove** takes the logo off." },
      { h: "Pick an accent color" },
      { ol: [
        "Under **Accent color**, click a swatch. **From your logo** shows colors taken from your logo. You can also pick any color, or type a hex code such as #2F6FB3.",
        "Check the **Preview**. **Button contrast** says **readable** or **too low**.",
        "Click **Save changes**, or **Undo** to go back.",
      ] },
      { p: "The accent colors headings, links, buttons and highlights while this company is selected. DocVault works out a darker shade for text and buttons so they stay readable." },
    ],
    good: [
      "You see your changes straight away. Others see them the next time they open DocVault.",
      "**All my companies** always uses DocVault's own colors.",
      "A very light color gets a warning, because icons on white would be hard to see.",
      "Every change to the name, logo or color is in the audit log.",
    ],
    related: ["switch-companies", "add-people", "roles"],
    keys: "company name rename display name logo brand branding accent color colour hex theme settings companies",
  },
];

export const articleById = (id: string) => ARTICLES.find((a) => a.id === id);
export const videoById = (id: string) => VIDEOS.find((v) => v.id === id);

/** Plain text of an article, for search. */
export function articleText(a: HelpArticle): string {
  const parts: string[] = [a.title, a.summary, a.applies, a.keys ?? "", ...(a.good ?? [])];
  for (const id of a.videos ?? []) { const v = videoById(id); if (v) parts.push(v.title, ...(v.chapters ?? []).map((c) => c[1])); }
  for (const b of a.body) {
    if ("h" in b) parts.push(b.h);
    else if ("p" in b) parts.push(b.p);
    else if ("ol" in b) parts.push(...b.ol);
    else if ("ul" in b) parts.push(...b.ul);
  }
  return parts.join(" ").replace(/\*\*/g, "").toLowerCase();
}

const STOP = new Set(["a", "an", "the", "i", "my", "do", "how", "to", "of", "in", "is", "what", "can"]);

/** Articles matching every word of the query, best first: the whole phrase, then title, then search words. */
/** What this person can see: admin-only articles are for company admins. */
export const visibleArticles = (admin: boolean) => ARTICLES.filter((a) => !a.admin || admin);

export function searchHelp(q: string, admin = false): HelpArticle[] {
  const words = q.toLowerCase().split(/\s+/).filter((w) => w && !STOP.has(w));
  if (!words.length) return [];
  const phrase = words.join(" ");
  return visibleArticles(admin)
    .map((a) => {
      const text = articleText(a), title = a.title.toLowerCase(), keys = (a.keys ?? "").toLowerCase();
      if (!words.every((w) => text.includes(w))) return null;
      const score = (title.includes(phrase) ? 8 : 0) + (keys.includes(phrase) ? 6 : 0) + (text.includes(phrase) ? 2 : 0)
        + words.filter((w) => title.includes(w)).length * 3 + words.filter((w) => keys.includes(w)).length * 2;
      return { a, score };
    })
    .filter((x): x is { a: HelpArticle; score: number } => !!x)
    .sort((x, y) => y.score - x.score)
    .map((x) => x.a);
}
